import axios from "axios";

// Strip any trailing slash so `${BACKEND_URL}/api` is always `.../api`
// and never `.../api` with a double slash. Defensive against
// REACT_APP_BACKEND_URL being saved with or without a trailing `/`.
const RAW = process.env.REACT_APP_BACKEND_URL || "";
const BACKEND_URL = RAW.replace(/\/+$/, "");

export const API = `${BACKEND_URL}/api`;

// ─── Token storage (Bearer fallback for cookie-blocked browsers) ───
// The backend returns an `access_token` in login/register/refresh responses
// and accepts either cookies OR `Authorization: Bearer <token>` headers.
// We store the token in localStorage and forward it on every request, so
// auth works reliably even when third-party cookies are blocked
// (Chrome incognito default, Safari ITP, certain corporate networks, etc).
// "Remember me" keeps the token in localStorage so it survives closing the
// browser or app; otherwise it lives in sessionStorage and goes with the tab.
const TOKEN_KEY = "jangle.auth.token";
function readToken(store) {
  try { return store.getItem(TOKEN_KEY) || null; } catch { return null; }
}
export function getAuthToken() {
  return readToken(localStorage) || readToken(sessionStorage);
}
export function isTokenRemembered() {
  return !!readToken(localStorage);
}
// `remember` picks the store; when omitted the token stays where it was.
export function setAuthToken(token, { remember } = {}) {
  const keep = remember ?? (readToken(sessionStorage) && !readToken(localStorage) ? false : true);
  for (const store of [localStorage, sessionStorage]) {
    try { store.removeItem(TOKEN_KEY); } catch { /* private mode / disabled — ignore */ }
  }
  if (!token) return;
  try { (keep ? localStorage : sessionStorage).setItem(TOKEN_KEY, token); } catch { /* ignore */ }
}

export const api = axios.create({
  baseURL: API,
  withCredentials: true, // still send cookies when available
  headers: { "Content-Type": "application/json" },
});

// Attach the Bearer token to every outgoing request when present.
api.interceptors.request.use((config) => {
  const token = getAuthToken();
  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  // Polls keep running in a background tab or a backgrounded app. Tell the
  // server, so the player isn't shown as Online while they're away.
  try {
    if (typeof document !== "undefined" && document.visibilityState === "hidden") {
      config.headers = config.headers || {};
      config.headers["X-Jangle-Hidden"] = "1";
    }
  } catch { /* ignore */ }
  return config;
});

// ─── Single-device sessions ───
// Signing in on one device signs the account out everywhere else. The
// backend reports that as a 401 with this detail (HTTP) or close code 4001
// (match WebSocket); either way we broadcast SESSION_ENDED_EVENT and
// AuthContext signs this device out with a short notice.
export const SESSION_SUPERSEDED = "session_superseded";
export const WS_CLOSE_SESSION_SUPERSEDED = 4001;
export const SESSION_ENDED_EVENT = "jangle:session-ended";
export const SUPERSEDED_MESSAGE = "Signed in on another device";

export function signalSessionSuperseded() {
  setAuthToken(null);
  try {
    window.dispatchEvent(new CustomEvent(SESSION_ENDED_EVENT, { detail: { reason: SESSION_SUPERSEDED } }));
  } catch { /* ignore */ }
}

// If a request comes back 401, our token is dead — wipe it so the app
// doesn't keep retrying with a stale Bearer.
api.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err?.response?.status === 401) {
      // Don't wipe on the login/register endpoints themselves — those return
      // 401 for bad credentials, not for an invalid bearer.
      const url = err?.config?.url || "";
      if (!/\/auth\/(login|register|refresh)$/.test(url)) {
        if (err?.response?.data?.detail === SESSION_SUPERSEDED) {
          signalSessionSuperseded();
        } else {
          setAuthToken(null);
        }
      }
    }
    return Promise.reject(err);
  },
);

// Field names FastAPI reports in a 422 validation error, and what to tell
// the player for each kind of problem.
const FIELD_MESSAGES = {
  email: { default: "That email address doesn't look right. Check it and try again." },
  username: {
    string_too_short: "Usernames need at least 2 characters.",
    string_too_long: "Usernames can be at most 24 characters.",
    missing: "Pick a username.",
    default: "That username can't be used. Try another one.",
  },
  password: {
    string_too_short: "Passwords need at least 6 characters.",
    string_too_long: "Passwords can be at most 128 characters.",
    missing: "Enter a password.",
    default: "That password can't be used. Try another one.",
  },
};

function describeValidation(items) {
  const msgs = items.map((e) => {
    const field = Array.isArray(e?.loc) ? e.loc[e.loc.length - 1] : null;
    const table = FIELD_MESSAGES[field];
    if (table) return table[e?.type] || table.default;
    return typeof e?.msg === "string" ? e.msg : null;
  }).filter(Boolean);
  return [...new Set(msgs)].join(" ");
}

// Turn any failed request into a sentence that says what actually went
// wrong: no connection, a server fault, a bad field, or the server's own
// message (username taken, wrong password, ...).
export function describeError(err) {
  const res = err?.response;
  if (!res && !err?.isAxiosError) {
    // Not a request failure at all: a bug in our own code after the call.
    return `Something went wrong on this page (${err?.message || "unknown error"}). Please reload and try again.`;
  }
  if (!res) {
    if (err?.code === "ECONNABORTED") {
      return "The Jangle server took too long to answer. Check your connection and try again.";
    }
    if (typeof navigator !== "undefined" && navigator.onLine === false) {
      return "You're offline. Connect to the internet and try again.";
    }
    return "Couldn't reach the Jangle server. Check your internet connection and try again.";
  }
  const detail = res.data?.detail;
  if (res.status === 422 && Array.isArray(detail)) {
    const msg = describeValidation(detail);
    if (msg) return msg;
  }
  if (res.status === 429) return "Too many tries in a row. Wait a minute, then try again.";
  if (typeof detail === "string" && detail) return detail;
  if (res.status >= 500) {
    return `The Jangle server hit a problem (error ${res.status}). Please try again in a minute.`;
  }
  if (detail != null) return formatApiError(detail);
  return `Something went wrong (error ${res.status}). Please try again.`;
}

export function formatApiError(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}
